import { Button } from '@mui/material';
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { JourneyLoadingScreen, StandardJourneyBrandedScreen } from '~/components/StandardJourney';
import JourneySupportDrawer from '~/components/StandardJourney/JourneySupportDrawer';
import type { CardInput, CardToken } from '~/services/modules/paymentGateway';
import { CARD_CHANGE_APP_IDS } from './cardChangeAppIds';
import CardChangeFormStep from './steps/CardChangeFormStep';
import CardChangeOutcomeStep from './steps/CardChangeOutcomeStep';
import CardChangeSummaryStep from './steps/CardChangeSummaryStep';
import { useCardChangeJourney } from './useCardChangeJourney';

type Props = {
  /** Injetável para stories/testes; o padrão tokeniza no Pagar.me. */
  tokenize?: (publicKey: string | null | undefined, card: CardInput) => Promise<CardToken>;
};

/**
 * Troca de cartão da assinatura no visual 2026 (Figma «E-commerce — 2026», node 2097:5628), sem barra de
 * progresso nem «Voltar», com «Ajuda». Orquestra `summary → form → processing → outcome`.
 */
export default function CardChangeJourneyPage({ tokenize }: Props) {
  const { contractId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { loadState, contract, step, outcome, failureMessage, changedCard, publicKey, publicKeyLoading, actions } =
    useCardChangeJourney({ contractHash: contractId, organizationId: searchParams.get('org'), tokenize });
  const [supportOpen, setSupportOpen] = useState(false);

  return (
    <StandardJourneyBrandedScreen
      appId={CARD_CHANGE_APP_IDS.screen}
      contentLayout={
        loadState !== 'ready' || step === 'processing' ? 'fullscreen' : step === 'summary' ? 'card' : 'checkout'
      }
      progressPercent={0}
      showBack={false}
      showCheckoutStepper={false}
      showHeaderProgressBar={false}
    >
      {loadState === 'loading' ? (
        <JourneyLoadingScreen appId={CARD_CHANGE_APP_IDS.loading} message="Carregando seu contrato…" />
      ) : null}

      {loadState === 'error' ? (
        <JourneyLoadingScreen
          action={
            <Button
              data-app-id={`${CARD_CHANGE_APP_IDS.loadError}-retry`}
              onClick={actions.reloadContract}
              size="large"
              variant="contained"
            >
              Tentar novamente
            </Button>
          }
          appId={CARD_CHANGE_APP_IDS.loadError}
          busy={false}
          description="Pode ser uma instabilidade momentânea. Tente novamente em alguns instantes."
          message="Não foi possível carregar seu contrato agora"
        />
      ) : null}

      {loadState === 'missing' ? (
        <JourneyLoadingScreen
          action={
            <Button
              data-app-id={CARD_CHANGE_APP_IDS.loadErrorSupport}
              onClick={() => setSupportOpen(true)}
              size="large"
              variant="contained"
            >
              Falar com o atendimento
            </Button>
          }
          appId={CARD_CHANGE_APP_IDS.loadMissing}
          busy={false}
          description="O link pode estar incompleto ou o contrato não está disponível. Fale com o atendimento para trocar o cartão."
          message="Não encontramos este contrato"
        />
      ) : null}

      {loadState === 'ready' && contract && step === 'summary' ? (
        <CardChangeSummaryStep
          contract={contract}
          onStart={actions.startCardChange}
          onSupport={() => setSupportOpen(true)}
        />
      ) : null}

      {loadState === 'ready' && contract && step === 'form' ? (
        <CardChangeFormStep
          billingAddress={contract.insured?.address ?? {}}
          onCardAdded={actions.submitCard}
          publicKey={publicKey}
          publicKeyLoading={publicKeyLoading}
          tokenize={tokenize}
        />
      ) : null}

      {loadState === 'ready' && step === 'processing' ? (
        <JourneyLoadingScreen appId={CARD_CHANGE_APP_IDS.processing} message="Trocando o cartão…" />
      ) : null}

      {loadState === 'ready' && step === 'outcome' && outcome ? (
        <CardChangeOutcomeStep
          card={changedCard}
          errorMessage={failureMessage}
          onRetry={actions.retry}
          onUseAnotherCard={actions.useAnotherCard}
          outcome={outcome}
          paymentMethod={contract?.payment_method}
        />
      ) : null}

      <JourneySupportDrawer
        appId={CARD_CHANGE_APP_IDS.supportDrawer}
        onClose={() => setSupportOpen(false)}
        open={supportOpen}
      />
    </StandardJourneyBrandedScreen>
  );
}
